import './Markdown.css';

import { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * A deliberately small Markdown renderer for prose authored in another repo:
 * error-code descriptions and service guides (docs/service-guides.md).
 *
 * It builds React elements directly — no `dangerouslySetInnerHTML`, so no HTML
 * from an ingested artifact can ever reach the DOM as markup.
 *
 * Supported: ATX headings, paragraphs, fenced code, blockquotes, thematic
 * breaks, single-level bullet and numbered lists (an indented continuation
 * line belongs to the item above it), GFM pipe tables; inline code, `**bold**`,
 * `*italic*` and `[text](href)`.
 *
 * Not supported, on purpose:
 *   - `_underscore_` emphasis — error codes are snake_case (`invalid_audience`)
 *     and would be mangled by it.
 *   - Nested lists, reference links, images, raw HTML. If a description needs
 *     one of those, the fix is to reword it in the owning repo or to extend
 *     this renderer — not to author HTML here.
 */

const INLINE = /(`[^`]+`)|(\*\*[^*]+?\*\*)|(\*[^*\s][^*]*?\*)|(\[[^\]]*?\]\([^()\s]+\))/g;

const SAFE_EXTERNAL = /^(https?:|mailto:)/i;

/**
 * Heading ids follow Docsify's slug rules, because that is what the producers'
 * own docs sites and link checkers use: an anchor an author verified there
 * resolves here too. Punctuation is dropped, whitespace becomes '-', and a
 * leading digit gets a '_' prefix.
 */
const PUNCT = /[\u2000-\u206F\u2E00-\u2E7F\\'!"#$%&()*+,./:;<=>?@[\]^`{|}~]/g;

function slugify(text: string): string {
  const slug = text
    .trim()
    .toLowerCase()
    .replace(/<[^>]+>/g, '')
    .replace(PUNCT, '')
    .replace(/\s/g, '-');
  return slug.replace(/^(\d)/, '_$1');
}

function renderLink(token: string, key: string): ReactNode {
  const split = token.indexOf('](');
  const label = token.slice(1, split);
  const href = token.slice(split + 2, -1);

  // An in-page anchor is the browser's job: same document, native scroll.
  if (href.startsWith('#')) {
    return (
      <a href={href} key={key}>
        {label}
      </a>
    );
  }
  // In-site links (notably other /errors/* pages) stay client-side.
  if (href.startsWith('/')) {
    return (
      <Link key={key} to={href}>
        {label}
      </Link>
    );
  }
  if (SAFE_EXTERNAL.test(href)) {
    return (
      <a href={href} key={key} rel="noreferrer" target="_blank">
        {label}
      </a>
    );
  }
  // Anything else (javascript:, data:, …) renders as inert text.
  return <span key={key}>{label}</span>;
}

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re = new RegExp(INLINE.source, 'g');
  let last = 0;
  let n = 0;
  let match = re.exec(text);

  while (match) {
    if (match.index > last) {
      nodes.push(text.slice(last, match.index));
    }
    const token = match[0];
    const key = `${keyPrefix}-i${n++}`;

    if (token.startsWith('`')) {
      nodes.push(
        <code className="md-code" key={key}>
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith('**')) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*')) {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    } else {
      nodes.push(renderLink(token, key));
    }

    last = match.index + token.length;
    match = re.exec(text);
  }

  if (last < text.length) {
    nodes.push(text.slice(last));
  }
  return nodes;
}

const BULLET = /^\s*[-*+]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const HEADING = /^(#{1,6})\s+(.*)$/;
const RULE = /^\s*([-*_])\s*\1\s*\1[\s\-*_]*$/;
/** A pipe-table row: starts with `|`. The row's cells are split on unescaped pipes. */
const TABLE_ROW = /^\s*\|.*$/;
/** The delimiter row under a table header: `| --- | :---: |`. */
const TABLE_DELIMITER = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;
/** A wrapped list item's continuation: indented, and not a list item itself. */
const CONTINUATION = /^\s{2,}\S/;

type Align = 'left' | 'center' | 'right' | undefined;

/** `| a | b \| c |` → ['a', 'b | c'] */
function splitCells(row: string): string[] {
  const trimmed = row.trim().replace(/^\|/, '').replace(/\|$/, '');
  const cells: string[] = [];
  let current = '';
  for (let i = 0; i < trimmed.length; i++) {
    const ch = trimmed[i];
    if (ch === '\\' && trimmed[i + 1] === '|') {
      current += '|';
      i++;
    } else if (ch === '|') {
      cells.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  cells.push(current.trim());
  return cells;
}

function alignments(delimiter: string): Align[] {
  return splitCells(delimiter).map((cell) => {
    const left = cell.startsWith(':');
    const right = cell.endsWith(':');
    return left && right ? 'center' : right ? 'right' : left ? 'left' : undefined;
  });
}

const cellStyle = (align: Align): CSSProperties | undefined =>
  align ? { textAlign: align } : undefined;

function renderBlocks(source: string, headingOffset: number): ReactNode[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let n = 0;
  const key = () => `b${n++}`;
  // Duplicate headings get '-1', '-2', … like Docsify, so every anchor is unique.
  const slugsSeen = new Map<string, number>();
  const headingId = (text: string) => {
    const base = slugify(text);
    const seen = slugsSeen.get(base);
    slugsSeen.set(base, (seen ?? 0) + 1);
    return seen === undefined ? base : `${base}-${seen}`;
  };

  const startsBlock = (line: string) =>
    !line.trim() ||
    line.trimStart().startsWith('```') ||
    line.trimStart().startsWith('>') ||
    HEADING.test(line) ||
    RULE.test(line) ||
    BULLET.test(line) ||
    NUMBERED.test(line) ||
    TABLE_ROW.test(line);

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i++;
      continue;
    }

    // ── Fenced code ────────────────────────────────────────────────────────
    if (line.trimStart().startsWith('```')) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) {
        body.push(lines[i]);
        i++;
      }
      i++; // closing fence (or end of input)
      blocks.push(
        <pre className="md-pre" key={key()}>
          <code>{body.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    // ── Thematic break ─────────────────────────────────────────────────────
    if (RULE.test(line)) {
      blocks.push(<hr className="md-rule" key={key()} />);
      i++;
      continue;
    }

    // ── Heading ────────────────────────────────────────────────────────────
    const heading = HEADING.exec(line);
    if (heading) {
      // Shifted by the caller's offset, and never above h2: h1 is the page's
      // own title in every context this renders in.
      const level = Math.min(Math.max(heading[1].length + headingOffset, 2), 6);
      const Tag = `h${level}` as 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      const k = key();
      blocks.push(
        <Tag className="md-heading" id={headingId(heading[2])} key={k}>
          {renderInline(heading[2], k)}
        </Tag>,
      );
      i++;
      continue;
    }

    // ── Blockquote ─────────────────────────────────────────────────────────
    if (line.trimStart().startsWith('>')) {
      const body: string[] = [];
      while (i < lines.length && lines[i].trimStart().startsWith('>')) {
        body.push(lines[i].trimStart().replace(/^>\s?/, ''));
        i++;
      }
      const k = key();
      blocks.push(
        <blockquote className="md-quote" key={k}>
          {renderInline(body.join(' '), k)}
        </blockquote>,
      );
      continue;
    }

    // ── Table ──────────────────────────────────────────────────────────────
    if (TABLE_ROW.test(line) && i + 1 < lines.length && TABLE_DELIMITER.test(lines[i + 1])) {
      const header = splitCells(line);
      const aligns = alignments(lines[i + 1]);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && TABLE_ROW.test(lines[i])) {
        rows.push(splitCells(lines[i]));
        i++;
      }
      const k = key();
      blocks.push(
        <table className="md-table" key={k}>
          <thead>
            <tr>
              {header.map((cell, c) => (
                <th key={`${k}-h${c}`} style={cellStyle(aligns[c])}>
                  {renderInline(cell, `${k}-h${c}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={`${k}-r${r}`}>
                {header.map((_, c) => (
                  <td key={`${k}-r${r}c${c}`} style={cellStyle(aligns[c])}>
                    {renderInline(row[c] ?? '', `${k}-r${r}c${c}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>,
      );
      continue;
    }

    // ── Lists ──────────────────────────────────────────────────────────────
    const listPattern = BULLET.test(line) ? BULLET : NUMBERED.test(line) ? NUMBERED : null;
    if (listPattern) {
      const items: string[] = [];
      while (i < lines.length) {
        if (listPattern.test(lines[i])) {
          items.push(listPattern.exec(lines[i])?.[1] ?? '');
        } else if (items.length && CONTINUATION.test(lines[i]) && !startsBlock(lines[i])) {
          items[items.length - 1] += ` ${lines[i].trim()}`;
        } else {
          break;
        }
        i++;
      }
      const k = key();
      const children = items.map((item, idx) => (
        <li key={`${k}-${idx}`}>{renderInline(item, `${k}-${idx}`)}</li>
      ));
      blocks.push(
        listPattern === BULLET ? (
          <ul className="md-list" key={k}>
            {children}
          </ul>
        ) : (
          <ol className="md-list" key={k}>
            {children}
          </ol>
        ),
      );
      continue;
    }

    // ── Paragraph ──────────────────────────────────────────────────────────
    // The first line is taken unconditionally: a line that looks like the
    // start of a block but was not one (a lone `| pipe |` row with no
    // delimiter under it) must still be consumed, or this loop never advances.
    const paragraph: string[] = [lines[i].trim()];
    i++;
    while (i < lines.length && !startsBlock(lines[i])) {
      paragraph.push(lines[i].trim());
      i++;
    }
    const k = key();
    blocks.push(
      <p className="md-paragraph" key={k}>
        {renderInline(paragraph.join(' '), k)}
      </p>,
    );
  }

  return blocks;
}

interface Props {
  source: string;
  /**
   * How many levels to push headings down. The default (2) suits prose that
   * sits under a page's own h1 and h2, like an error description; a guide,
   * whose body starts at `##`, passes 0. Headings never render above h2.
   */
  headingOffset?: number;
  /** Extra class on the wrapper, for a variant such as `md--article`. */
  className?: string;
}

export default function Markdown({ source, headingOffset = 2, className }: Props) {
  return (
    <div className={['md', className].filter(Boolean).join(' ')}>
      {renderBlocks(source, headingOffset)}
    </div>
  );
}
